/**
 * Geometry of a radial menu in screen pixels, centred on (0, 0) with y
 * pointing down. Angles are radians measured clockwise from straight up, so
 * sector 0 is centred on the top of the circle.
 */
import type { Point } from '~/components/toolbar/pointerMenu/PointerMenu';

/** The angle one of `count` equal sectors spans. */
export function sectorSpan(count: number): number {
    return (2 * Math.PI) / count;
}

/** The angle at the centre of sector `index` of `count`. */
export function sectorCenterAngle(index: number, count: number): number {
    return index * sectorSpan(count);
}

/** The point at distance `radius` from the centre in direction `angle`. */
export function polar(radius: number, angle: number): Point {
    return { x: Math.sin(angle) * radius, y: -Math.cos(angle) * radius };
}

/**
 * The sector the offset `(dx, dy)` from the centre points into, or null
 * inside the dead zone, beyond `maxRadius`, or with no sectors. The
 * direction alone picks the sector, so a flick past the menu's edge still
 * lands on the sector it passed through.
 */
export function sectorAt(
    dx: number,
    dy: number,
    count: number,
    deadZoneRadius: number,
    maxRadius = Infinity
): number | null {
    if (count <= 0) {
        return null;
    }
    const distance = Math.hypot(dx, dy);
    if (distance < deadZoneRadius || distance > maxRadius) {
        return null;
    }
    const span = sectorSpan(count);
    const angle = Math.atan2(dx, -dy);
    const clockwise = angle < 0 ? angle + 2 * Math.PI : angle;
    return Math.floor((clockwise + span / 2) / span) % count;
}

/** SVG path of the ring slice between radii `inner` and `outer` from angle `from` to `to`. */
export function wedgePath(
    inner: number,
    outer: number,
    from: number,
    to: number
): string {
    const largeArc = to - from > Math.PI ? 1 : 0;
    const o0 = polar(outer, from);
    const o1 = polar(outer, to);
    const i1 = polar(inner, to);
    const i0 = polar(inner, from);
    return [
        `M ${o0.x} ${o0.y}`,
        `A ${outer} ${outer} 0 ${largeArc} 1 ${o1.x} ${o1.y}`,
        `L ${i1.x} ${i1.y}`,
        `A ${inner} ${inner} 0 ${largeArc} 0 ${i0.x} ${i0.y}`,
        'Z',
    ].join(' ');
}

/**
 * The centre that keeps a menu of `radius` wholly inside a `width` x
 * `height` window with `margin` to spare, as near `point` as it can be.
 */
export function clampCenter(
    point: Point,
    radius: number,
    width: number,
    height: number,
    margin = 8
): Point {
    const clamp = (value: number, size: number) => {
        const min = radius + margin;
        const max = size - radius - margin;
        return min > max ? size / 2 : Math.min(Math.max(value, min), max);
    };
    return { x: clamp(point.x, width), y: clamp(point.y, height) };
}
